import React, { useState } from "react";
import { Box, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";

import Panel from "../../components/grid/Panel";
import PagedView from "../../components/PagedView";
import FunnelChart from "../../components/charts/FunnelChart";
import JourneyGraph from "../../components/charts/JourneyGraph";
import JourneyStepper from "../../components/charts/JourneyStepper";
import BarList from "../../components/charts/BarList";
import CaveatNote from "../../components/charts/CaveatNote";
import CountUp from "../../components/CountUp";
import { brand, fonts } from "../../themes/theme";
import { useVariant } from "../../variants/useVariant";
import { resolveByType } from "../../tenant/tenant";
import journeyData from "../../data/journey.json";

/**
 * Vista Fuga por fase (HU-2).
 * Todo cabe en el viewport (sin scroll en desktop).
 *
 * El recorrido real de fertilidad no es lineal (tiene ramas, sub-procesos y un
 * ciclo TRA recurrente), asi que hay 2 visualizaciones (toggle en la esquina):
 * - Embudo: nivel generico (6 fases) con drill-down. Las fases con desglose
 *   (A-H, ver journey.json) son clicables; algunas abren VARIOS sub-embudos a
 *   la vez (se navegan con pestañas internas). Boton "volver" para salir.
 * - Grafo: el recorrido completo como grafo de estados (bucles, rama paralela
 *   de embriones congelados, salidas), sin necesidad de clic — ver JourneyGraph.
 */
function JourneyScreen() {
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const { variantId, setVariant } = useVariant("journey");
    const chartKind = variantId === "graph" ? "graph" : "funnel";
    const [activeLevel, setActiveLevel] = useState(null);
    const [levelGroup, setLevelGroup] = useState(null);
    const data = resolveByType(journeyData);

    const handleModeChange = (id) => {
        setActiveLevel(null);
        setLevelGroup(null);
        setVariant(id);
    };

    const handleDrill = (drillTo, title) => {
        if (Array.isArray(drillTo)) {
            setLevelGroup({ ids: drillTo, title });
            setActiveLevel(null);
        } else {
            setLevelGroup(null);
            setActiveLevel(drillTo);
        }
    };

    const handleBack = () => {
        setActiveLevel(null);
        setLevelGroup(null);
    };

    const funnelRows = data.root.funnel.map((f) => ({
        label: t(`phases.${f.phaseKey}`),
        retentionPct: f.retentionPct,
        count: f.count,
        caidaPp: f.dropPp,
        note: f.note ? t(`views.journey.notes.${f.note}`) : null,
        drillTo: f.drillTo,
        noData: f.noData,
    }));

    const hangingRow = data.root.funnel.find((f) => f.extraDrillTo);

    const level = activeLevel ? data.levels[activeLevel] : null;
    const base = `views.journey.levels.${activeLevel}`;

    const chartTitle = chartKind === "graph"
        ? t("views.journey.graphTitle")
        : levelGroup
            ? levelGroup.title
            : level
                ? t(`${base}.title`)
                : t("views.journey.funnelTitle");
    const chartSubtitle = chartKind === "graph"
        ? t("views.journey.graphSubtitle")
        : levelGroup
            ? t("views.journey.groupSubtitle")
            : level
                ? t(`${base}.subtitle`)
                : t("views.journey.funnelSubtitle");

    return (
        <Box sx={{ flex: { xs: "0 0 auto", md: 1 }, height: { xs: "auto", md: "100%" }, minHeight: 0, display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Box sx={{ flex: { xs: "0 0 auto", md: 1 }, minHeight: 0, display: "flex" }}>
                <Panel
                    span={12}
                    dense
                    title={chartTitle}
                    subtitle={chartSubtitle}
                    action={
                        level || levelGroup ? (
                            <BackButton label={t("views.journey.back")} onClick={handleBack} />
                        ) : (
                            <ChartModeToggle
                                value={chartKind}
                                onChange={handleModeChange}
                                funnelLabel={t("views.journey.variants.funnel")}
                                graphLabel={t("views.journey.variants.graph")}
                            />
                        )
                    }
                    sx={{ flex: { xs: "0 0 auto", md: 1 }, minHeight: 0, width: "100%" }}
                    fill={!isMobile}
                >
                    {chartKind === "graph" ? (
                        <JourneyGraph />
                    ) : levelGroup ? (
                        <GroupedLevels ids={levelGroup.ids} t={t} onDrill={handleDrill} mobile={isMobile} />
                    ) : level ? (
                        <LevelContent levelId={activeLevel} level={level} base={base} t={t} onDrill={handleDrill} inGroup={isMobile} />
                    ) : (
                        <Box sx={{ height: { xs: "auto", md: "100%" }, display: "flex", flexDirection: "column", gap: 1, minHeight: 0 }}>
                            <Box sx={{ flex: { xs: "0 0 auto", md: 1 }, minHeight: 0 }}>
                                <FunnelChart data={funnelRows} barHeight={28} fill={!isMobile} dropUnit="%" onRowClick={(row) => handleDrill(row.drillTo, row.label)} />
                            </Box>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
                                <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>{t("views.journey.flowNote")}</Typography>
                                {hangingRow && (
                                    <HangingLink
                                        label={t("views.journey.criopreservacionLabel")}
                                        onClick={() => handleDrill(hangingRow.extraDrillTo)}
                                    />
                                )}
                            </Box>
                        </Box>
                    )}
                </Panel>
            </Box>
        </Box>
    );
}

function HangingLink({ label, onClick }) {
    return (
        <Box
            component="button"
            type="button"
            onClick={onClick}
            sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.4,
                border: "1px dashed",
                borderColor: brand.teal,
                bgcolor: "rgba(173,202,214,0.12)",
                borderRadius: 1.5,
                px: 1,
                py: 0.3,
                cursor: "pointer",
                fontFamily: fonts.body,
                fontSize: 11.5,
                fontWeight: 500,
                color: "text.primary",
                flexShrink: 0,
                "&:hover": { bgcolor: "rgba(173,202,214,0.22)" },
            }}
        >
            {label} →
        </Box>
    );
}

/** Renderiza el contenido de un sub-embudo segun su `kind`.
 * `inGroup`: se muestra apilado con otros niveles (alto natural, no 100%). */
function LevelContent({ levelId, level, base, t, onDrill, inGroup }) {
    if (level.kind === "reasons") {
        return <ReasonsLevel level={level} base={base} t={t} inGroup={inGroup} />;
    }
    if (level.kind === "nested") {
        return <NestedLevel level={level} base={base} t={t} inGroup={inGroup} />;
    }
    if (level.kind === "funnel") {
        return <FunnelLevel level={level} base={base} t={t} inGroup={inGroup} />;
    }
    if (level.kind === "reasonsMulti") {
        return <ReasonsMultiLevel level={level} base={base} t={t} inGroup={inGroup} />;
    }
    if (level.kind === "steps") {
        return <StepsLevel level={level} base={base} t={t} onDrill={onDrill} inGroup={inGroup} />;
    }
    if (level.kind === "crio") {
        return <CrioLevel level={level} base={base} t={t} inGroup={inGroup} />;
    }
    if (level.kind === "beta") {
        return <BetaLevel level={level} base={base} t={t} inGroup={inGroup} />;
    }
    return null;
}

/** Varios niveles de una misma fase: una tarjeta propia por seccion, con un
 * indice numerado para distinguirlas. Nunca scroll: si las 3 caben juntas se
 * muestran apiladas (PagedView las mide); si no, se pagina una a una con
 * flechas + puntos. */
function GroupedLevels({ ids, t, onDrill, mobile }) {
    const data = resolveByType(journeyData);
    const pages = ids.map((id, i) => {
        const level = data.levels[id];
        const base = `views.journey.levels.${id}`;
        return (
            <Box
                key={id}
                sx={{
                    border: "1px solid",
                    borderColor: brand.gray100,
                    borderRadius: "12px",
                    p: { xs: 1.5, md: 1.75 },
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.25 }}>
                    <Box
                        sx={{
                            width: 22,
                            height: 22,
                            borderRadius: "50%",
                            bgcolor: "rgba(50,72,202,0.09)",
                            color: "primary.main",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontFamily: fonts.body,
                            fontSize: 12,
                            fontWeight: 600,
                            flexShrink: 0,
                        }}
                    >
                        {i + 1}
                    </Box>
                    <Typography sx={{ fontFamily: fonts.display, fontWeight: 400, fontSize: 15.5, color: "text.primary", lineHeight: 1.3 }}>
                        {t(`${base}.tabLabel`)}
                    </Typography>
                </Box>
                <LevelContent levelId={id} level={level} base={base} t={t} onDrill={onDrill} inGroup />
            </Box>
        );
    });
    return <PagedView pages={pages} gap={1.25} stack={mobile} />;
}

/** Linea de nota secundaria (predictores, contexto): barra izquierda para
 * distinguirla visualmente de los avisos metodologicos (CaveatNote, gris con
 * icono). `accent` la resalta en azul cuando es un hallazgo relevante. */
function NoteLine({ children, accent }) {
    return (
        <Typography
            sx={{
                fontSize: 11.5,
                color: "text.secondary",
                lineHeight: 1.45,
                pl: 1.1,
                borderLeft: "2px solid",
                borderColor: accent ? "primary.main" : brand.gray200,
            }}
        >
            {children}
        </Typography>
    );
}

/** Señales observables por fase (predictores/marcadores): chips diferenciados
 * entre dato de CRM/estructurado (neutro) y señal narrativa de texto/audio que
 * capta Mential (azul). Aparece solo dentro del drill de un punto de fuga, para
 * no ensuciar el embudo/grafo. */
function MarkersSection({ level, base, t }) {
    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.6, mt: 0.5, pt: 0.75, borderTop: "1px solid", borderColor: "divider" }}>
            <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1, justifyContent: "space-between" }}>
                <Typography sx={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: "0.05em", color: brand.gray500, fontWeight: 600 }}>
                    {t("views.journey.markersTitle")}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                    <LegendDot color={brand.gray200} label={t("views.journey.markersLegendCrm")} />
                    <LegendDot color={brand.primary} label={t("views.journey.markersLegendMential")} />
                </Box>
            </Box>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.6 }}>
                {level.markers.map((m) => {
                    const mential = m.origin === "mential";
                    return (
                        <Typography
                            key={m.key}
                            sx={{
                                fontSize: 10.5,
                                lineHeight: 1.3,
                                color: mential ? "primary.main" : brand.navyLight,
                                bgcolor: mential ? "rgba(50,72,202,0.07)" : brand.gray50,
                                border: "1px solid",
                                borderColor: mential ? "rgba(50,72,202,0.25)" : brand.gray100,
                                borderRadius: "999px",
                                px: 0.9,
                                py: 0.3,
                            }}
                        >
                            {t(`${base}.markers.${m.key}`)}
                        </Typography>
                    );
                })}
            </Box>
        </Box>
    );
}

function LegendDot({ color, label }) {
    return (
        <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.4 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: color, flexShrink: 0 }} />
            <Typography sx={{ fontSize: 9.5, color: brand.gray500 }}>{label}</Typography>
        </Box>
    );
}

// A, B, D — lista de razones (BarList). Suman >100% (multi-causa): caveat fijo.
// La razon dominante (primera, mayor %) se resalta para que se lea de un vistazo.
function ReasonsLevel({ level, base, t, inGroup }) {
    const items = level.reasons.map((r, i) => ({
        label: t(`${base}.reasons.${r.key}`),
        pct: r.pct,
        value: `${r.pct}%`,
        color: i === 0 ? brand.primary : "rgba(50,72,202,0.22)",
        strong: i === 0,
    }));
    return (
        <Box sx={{ height: inGroup ? "auto" : "100%", display: "flex", flexDirection: "column", gap: 1.25, justifyContent: "flex-start" }}>
            <BarList items={items} />
            {level.shareNote && (
                <Box
                    sx={{
                        bgcolor: "rgba(50,72,202,0.06)",
                        border: "1px solid rgba(50,72,202,0.18)",
                        borderRadius: "8px",
                        px: 1.25,
                        py: 0.75,
                    }}
                >
                    <Typography sx={{ fontSize: 13, fontWeight: 500, color: "text.primary", lineHeight: 1.4 }}>
                        {t(`${base}.${level.shareNote}`)}
                    </Typography>
                </Box>
            )}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.6 }}>
                <CaveatNote dense>{t("views.journey.reasonsCaveat")}</CaveatNote>
                {level.categoryNote && <NoteLine>{t(`${base}.${level.categoryNote}`)}</NoteLine>}
                {level.predictors && <NoteLine accent>{t(`${base}.${level.predictors}`)}</NoteLine>}
            </Box>
            {level.markers && <MarkersSection level={level} base={base} t={t} />}
        </Box>
    );
}

// C — no continuacion IIU: stat + split clinica/paciente + caveat obligatorio + razones del subgrupo.
// El lado mayoritario del split y la razon dominante se resaltan igual que en ReasonsLevel.
function NestedLevel({ level, base, t, inGroup }) {
    const clinicaWins = level.split.clinica >= level.split.paciente;
    const splitItems = [
        {
            label: t(`${base}.splitClinica`),
            pct: level.split.clinica,
            value: `${level.split.clinica}%`,
            color: clinicaWins ? brand.primary : "rgba(50,72,202,0.22)",
            strong: clinicaWins,
        },
        {
            label: t(`${base}.splitPaciente`),
            pct: level.split.paciente,
            value: `${level.split.paciente}%`,
            color: clinicaWins ? "rgba(50,72,202,0.22)" : brand.primary,
            strong: !clinicaWins,
        },
    ];
    const reasonItems = level.reasons.map((r, i) => ({
        label: t(`${base}.reasons.${r.key}`),
        pct: r.pct,
        value: `${r.pct}%`,
        color: i === 0 ? brand.primary : "rgba(50,72,202,0.22)",
        strong: i === 0,
    }));
    return (
        <Box sx={{ height: inGroup ? "auto" : "100%", display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: { xs: 1.5, md: 3 } }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
                    <Typography sx={{ fontFamily: fonts.display, fontWeight: 400, fontSize: 28, color: "primary.main" }}>
                        <CountUp to={level.stat.pct} suffix="%" />
                    </Typography>
                    <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>
                        {t(`${base}.statLabel`)} ({level.stat.count}/{level.stat.base})
                    </Typography>
                </Box>
                <BarList items={splitItems} />
                <CaveatNote dense>{t(`${base}.caveatC`)}</CaveatNote>
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, borderLeft: { md: "1px solid" }, borderColor: { md: "divider" }, pl: { md: 3 } }}>
                <Typography sx={{ fontSize: 12.5, color: "text.secondary", fontWeight: 500 }}>{t(`${base}.razonesDecisionPareja`)}</Typography>
                <BarList items={reasonItems} />
                <NoteLine accent>{t(`${base}.predictoresC`)}</NoteLine>
            </Box>
        </Box>
    );
}

// E — retencion ciclo a ciclo (embudo clasico) + acumulada + horizonte Troude separado.
function FunnelLevel({ level, base, t, inGroup }) {
    const rows = level.funnel.map((s) => ({
        label: t(`${base}.steps.${s.stepKey}`),
        retentionPct: Math.round(s.retentionPct),
        count: null,
        caidaPp: s.dropPp != null ? Math.round(s.dropPp * 10) / 10 : null,
        critical: s.critical,
        note: s.note ? t(`${base}.notes.${s.note}`) : null,
    }));
    return (
        <Box sx={{ height: inGroup ? "auto" : "100%", display: "flex", flexDirection: "column", gap: 1, minHeight: 0 }}>
            <Box sx={{ flex: inGroup ? "0 0 auto" : 1, height: inGroup ? 150 : undefined, minHeight: 0 }}>
                <FunnelChart data={rows} barHeight={30} fill dropUnit="%" />
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, flexShrink: 0 }}>
                <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
                    <Typography sx={{ fontFamily: fonts.display, fontWeight: 400, fontSize: 20, color: "primary.main" }}>
                        <CountUp to={level.cumulative.pct} suffix="%" />
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: "text.secondary" }}>{t(`${base}.${level.cumulative.labelKey}`)}</Typography>
                </Box>
                <CaveatNote dense>{t(`${base}.${level.cumulative.caveat}`)}</CaveatNote>
                <Box sx={{ mt: 0.5, pt: 0.5, borderTop: "1px solid", borderColor: "divider", display: "flex", alignItems: "baseline", gap: 1 }}>
                    <Typography sx={{ fontFamily: fonts.display, fontWeight: 400, fontSize: 20, color: "text.primary" }}>
                        <CountUp to={level.longHorizon.pct} suffix="%" />
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                        {t(`${base}.${level.longHorizon.labelKey}`)} ({level.longHorizon.count}/{level.longHorizon.base})
                    </Typography>
                </Box>
                <CaveatNote dense>{t(`${base}.${level.longHorizon.caveat}`)}</CaveatNote>
            </Box>
        </Box>
    );
}

// F — razones de fuga tras ciclos TRA: 2 grupos de barras (horizontes distintos).
function ReasonsMultiLevel({ level, base, t, inGroup }) {
    return (
        <Box sx={{ height: inGroup ? "auto" : "100%", display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: { xs: 1.5, md: 3 } }}>
            {level.groups.map((g, i) => {
                const items = g.reasons.map((r, ri) => ({
                    label: t(`${base}.reasons.${r.key}`),
                    pct: r.pct,
                    value: `${r.pct}%`,
                    color: ri === 0 ? brand.primary : "rgba(50,72,202,0.22)",
                    strong: ri === 0,
                }));
                return (
                    <Box
                        key={g.labelKey}
                        sx={{ display: "flex", flexDirection: "column", gap: 1, borderLeft: i > 0 ? { md: "1px solid" } : undefined, borderColor: { md: "divider" }, pl: i > 0 ? { md: 3 } : 0 }}
                    >
                        <Typography sx={{ fontSize: 12.5, color: "text.secondary", fontWeight: 500 }}>{t(`${base}.groups.${g.labelKey}`)}</Typography>
                        <BarList items={items} />
                        {g.caveat && <CaveatNote dense>{t(`${base}.${g.caveat}`)}</CaveatNote>}
                    </Box>
                );
            })}
            <Box sx={{ gridColumn: { xs: "1", md: "1 / -1" }, display: "flex", flexDirection: "column", gap: 0.5 }}>
                {level.secondCycleNote && <NoteLine>{t(`${base}.${level.secondCycleNote}`)}</NoteLine>}
                {level.predictors && <NoteLine accent>{t(`${base}.${level.predictors}`)}</NoteLine>}
                {level.markers && <MarkersSection level={level} base={base} t={t} />}
            </Box>
        </Box>
    );
}

// G — sub-proceso interno del Ciclo TRA (secuencia con 2 fugas medicas sin dato).
// El ultimo paso (Resultado de beta) enlaza al nivel J si tiene drillTo.
function StepsLevel({ level, base, t, onDrill, inGroup }) {
    const steps = level.steps.map((s) => ({
        label: t(`${base}.steps.${s.stepKey}`),
        leak: s.leakKey ? t(`${base}.leaks.${s.leakKey}`) : null,
        clickable: Boolean(s.drillTo),
        continueHint: s.drillTo ? t("views.journey.verResultado") : null,
        onClick: s.drillTo ? () => onDrill(s.drillTo) : undefined,
    }));
    return (
        <Box sx={{ height: inGroup ? "auto" : "100%", display: "flex", flexDirection: "column", gap: 1 }}>
            <Box sx={{ flex: inGroup ? "0 0 auto" : 1, height: inGroup ? 210 : undefined, minHeight: 0 }}>
                <JourneyStepper steps={steps} />
            </Box>
        </Box>
    );
}

// I — Criopreservacion: rama paralela, no bloquea el resultado. Dos bucles
// (vuelven al Ciclo TRA) + destino sin dato cuantificado.
function CrioLevel({ level, base, t, inGroup }) {
    return (
        <Box sx={{ height: inGroup ? "auto" : "100%", display: "flex", flexDirection: "column", gap: 1.25, justifyContent: "flex-start" }}>
            {level.contextNote && (
                <Typography sx={{ fontSize: 12.5, color: "text.secondary", lineHeight: 1.5 }}>{t(`${base}.${level.contextNote}`)}</Typography>
            )}
            <Box component="ul" sx={{ m: 0, p: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 1.25 }}>
                {level.loops.map((k) => (
                    <Box
                        key={k}
                        component="li"
                        sx={{ fontSize: 13, color: "text.primary", lineHeight: 1.5, pl: 1.25, borderLeft: "2px solid", borderColor: "teal.main" }}
                    >
                        {t(`${base}.loops.${k}`)}
                    </Box>
                ))}
            </Box>
            {level.concurrencyNote && <CaveatNote>{t(`${base}.${level.concurrencyNote}`)}</CaveatNote>}
            <Box>
                <Typography sx={{ fontSize: 12.5, color: "text.secondary", fontWeight: 500, mb: 0.4 }}>{t(`${base}.destinoTitle`)}</Typography>
                <Typography sx={{ fontSize: 13, color: "text.primary" }}>{t(`${base}.destinoOptions`)}</Typography>
            </Box>
        </Box>
    );
}

// J — Resultado de Beta: positiva (bucles) / negativa (bucles + fin de
// tratamiento con split clinica/paciente y caveat obligatorio, anidado).
function BetaLevel({ level, base, t, inGroup }) {
    const negStats = level.negativa.stats.map((s) => `${s.cyclePct}% ${t(`${base}.negativa.${s.labelKey}`)}`);
    const finSplitItems = [
        { label: t(`${base}.negativa.splitClinica`), pct: level.negativa.split.clinica, value: `${level.negativa.split.clinica}%` },
        { label: t(`${base}.negativa.splitPaciente`), pct: level.negativa.split.paciente, value: `${level.negativa.split.paciente}%` },
    ];
    return (
        <Box sx={{ height: inGroup ? "auto" : "100%", display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: { xs: 2, md: 3 } }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Typography sx={{ fontFamily: fonts.display, fontWeight: 400, fontSize: 15, color: "text.primary" }}>
                    {t(`${base}.positivaTitle`)}
                </Typography>
                {level.positiva.bullets.map((b) => (
                    <Typography key={b} sx={{ fontSize: 12.5, color: "text.secondary", lineHeight: 1.45, pl: 1.25, borderLeft: "2px solid", borderColor: "divider" }}>
                        {t(`${base}.positiva.${b}`)}
                    </Typography>
                ))}
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, borderLeft: { md: "1px solid" }, borderColor: { md: "divider" }, pl: { md: 3 } }}>
                <Typography sx={{ fontFamily: fonts.display, fontWeight: 400, fontSize: 15, color: "text.primary" }}>
                    {t(`${base}.negativaTitle`)}
                </Typography>
                {negStats.map((s) => (
                    <Typography key={s} sx={{ fontSize: 13, color: "primary.main" }}>{s}</Typography>
                ))}
                <Typography sx={{ fontSize: 12.5, color: "text.secondary", lineHeight: 1.45, pl: 1.25, borderLeft: "2px solid", borderColor: "divider" }}>
                    {t(`${base}.negativa.cicloSiguiente`)}
                </Typography>
                <Typography sx={{ fontSize: 12.5, color: "text.secondary", lineHeight: 1.45, pl: 1.25, borderLeft: "2px solid", borderColor: "divider" }}>
                    {t(`${base}.negativa.pausaReincorpora`)}
                </Typography>
                <Box sx={{ mt: 0.5, pt: 0.75, borderTop: "1px solid", borderColor: "divider" }}>
                    <Typography sx={{ fontSize: 12.5, color: "text.primary", fontWeight: 500, mb: 0.5 }}>
                        {t(`${base}.negativa.finTratamientoTitle`)}
                    </Typography>
                    <BarList items={finSplitItems} />
                    <Box sx={{ mt: 0.75 }}>
                        <CaveatNote dense>{t(`${base}.negativa.caveatFinTto`)}</CaveatNote>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}

function BackButton({ label, onClick }) {
    return (
        <Box
            component="button"
            type="button"
            onClick={onClick}
            sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                border: 0,
                bgcolor: "transparent",
                cursor: "pointer",
                px: 1,
                py: 0.5,
                borderRadius: 1.5,
                fontFamily: fonts.body,
                fontSize: 12.5,
                fontWeight: 500,
                color: "primary.main",
                flexShrink: 0,
                "&:hover": { bgcolor: "rgba(50,72,202,0.06)" },
            }}
        >
            <ArrowBackRoundedIcon sx={{ fontSize: 15 }} />
            {label}
        </Box>
    );
}

function ChartModeToggle({ value, onChange, funnelLabel, graphLabel }) {
    const options = [
        { id: "funnel", label: funnelLabel },
        { id: "graph", label: graphLabel },
    ];

    return (
        <Box
            role="tablist"
            aria-label="Modo de grafico"
            sx={{
                display: "inline-flex",
                p: 0.35,
                borderRadius: 2,
                bgcolor: brand.gray100,
                flexShrink: 0,
            }}
        >
            {options.map((opt) => {
                const active = value === opt.id;
                return (
                    <Box
                        key={opt.id}
                        component="button"
                        type="button"
                        role="tab"
                        aria-selected={active}
                        onClick={() => onChange(opt.id)}
                        sx={{
                            border: 0,
                            cursor: "pointer",
                            px: 1.25,
                            py: 0.5,
                            borderRadius: 1.5,
                            fontFamily: fonts.body,
                            fontSize: 12.5,
                            fontWeight: 500,
                            lineHeight: 1.3,
                            bgcolor: active ? brand.white : "transparent",
                            color: active ? brand.navy : brand.gray500,
                            boxShadow: active ? "0 1px 2px rgba(10,36,62,.08)" : "none",
                            transition: "background .15s, color .15s, box-shadow .15s",
                            "&:hover": { color: brand.navy },
                        }}
                    >
                        {opt.label}
                    </Box>
                );
            })}
        </Box>
    );
}

export const journeyVariants = [
    {
        id: "funnel",
        labelKey: "views.journey.variants.funnel",
        render: () => <JourneyScreen />,
    },
    {
        id: "graph",
        labelKey: "views.journey.variants.graph",
        render: () => <JourneyScreen />,
    },
];

export default journeyVariants;
